/**
 * FilmSearch Component
 * Film search with autocomplete for finding films to compare
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  Search,
  Movie,
  CalendarToday
} from '@mui/icons-material';
import { formatFilmTitle } from '../../lib/utils/compFormatters';
import trackedFilmsService from '../../lib/services/trackedFilmsService';

const FilmSearch = ({ onFilmSelect, loading = false, error = null }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Handle search input change
  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
  };

  // Handle film selection
  const handleFilmSelect = (film) => {
    onFilmSelect(film);
    setSearchResults([]);
    setSearchQuery('');
    setSearchError(null);
  };

  // Perform asynchronous search when query changes
  useEffect(() => {
    let isActive = true;

    const performSearch = async () => {
      const trimmed = searchQuery.trim();

      if (trimmed.length < 2) {
        setSearchResults([]);
        setSearchError(null);
        return;
      }

      setSearchLoading(true);
      setSearchError(null);

      try {
        const results = await trackedFilmsService.searchTrackedFilms(trimmed);
        if (!isActive) return;
        setSearchResults(results);
        
        // Debug: Log search results for troubleshooting
        if (trimmed.includes('venom') || trimmed.includes('tt16366836')) {
          console.log('🔍 Search results for "Venom" query:', {
            query: trimmed,
            resultsCount: results.length,
            results: results.map(r => ({ title: r.title, imdbId: r.imdbId }))
          });
        }
      } catch (err) {
        if (!isActive) return;
        console.error('❌ Film search failed:', err);
        setSearchError('Failed to search films. Please try again.');
        setSearchResults([]);
      } finally {
        if (isActive) {
          setSearchLoading(false);
        }
      }
    };

    performSearch();

    return () => {
      isActive = false;
    };
  }, [searchQuery]);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Search for a Film to Compare
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Search for a film that you want to compare with the current film. 
        You can search by title or IMDB ID for films in Film Tracking and Legacy Quorum.
      </Typography>

      {/* Search Input */}
      <TextField
        fullWidth
        placeholder="Search for a film (e.g., 'The Dark Knight', 'Action', 'Sci-Fi')"
        value={searchQuery}
        onChange={handleSearchChange}
        disabled={loading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {searchLoading ? <CircularProgress size={20} /> : <Search />}
            </InputAdornment>
          )
        }}
        sx={{ mb: 2 }}
      />

      {/* Error Display */}
      {(error || searchError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || searchError}
        </Alert>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
          <List>
            {searchResults.map((film, index) => (
              <ListItem key={`${film.source}-${film.imdbId}-${index}`} disablePadding>
                <ListItemButton
                  onClick={() => handleFilmSelect(film)}
                  disabled={loading}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      '& .MuiListItemText-primary': {
                        color: 'primary.contrastText'
                      }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Movie sx={{ color: 'text.secondary', fontSize: '1.5rem' }} />
                    
                    <Box sx={{ flexGrow: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {formatFilmTitle(film.title, 60)}
                            </Typography>
                            <Tooltip title={film.sourceLabel}>
                              <Chip
                                label={film.sourceLabel}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem'
                                }}
                              />
                            </Tooltip>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <CalendarToday sx={{ fontSize: '0.8rem', color: 'text.disabled' }} />
                              <Typography variant="caption" color="text.secondary">
                                {film.releaseYear || 'Unknown Year'}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={film.imdbId}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  borderColor: 'primary.light',
                                  color: 'primary.main'
                                }}
                              />
                              {film.metadata?.weeksUntilRelease !== null && film.metadata?.weeksUntilRelease !== undefined && (
                                <Chip
                                  label={`${film.metadata.weeksUntilRelease} wks to release`}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem'
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </Box>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* No Results */}
      {searchQuery.length >= 2 && searchResults.length === 0 && !searchLoading && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            No films found for "{searchQuery}"
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try searching with different keywords or check the spelling.
          </Typography>
        </Paper>
      )}

      {/* Search Tips */}
      {searchQuery.length === 0 && (
        <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Search Tips:</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            • Search by film title (e.g., "The Dark Knight")<br/>
            • Search by IMDB ID (e.g., "tt0468569")<br/>
            • Results include Film Tracking and Legacy Quorum titles
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default FilmSearch;
